import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table, Spinner, Alert, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getTickerTransactions } from '../services/api';
import { formatDate } from '../utils/dateUtils';
import { createLogger } from '../utils/debugLogger';

const logger = createLogger('tickerTransactionHistoryModal', 'Ticker transaction history modal', false, 'DEBUG');

const TickerTransactionHistoryModal = ({ show, onHide, tickerSymbol, accountId = null, accountName = null }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [transactionData, setTransactionData] = useState(null);

  const fetchTransactionHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await getTickerTransactions(tickerSymbol, accountId);
      setTransactionData(response.data);
    } catch (err) {
      logger.error('Error fetching ticker transaction history:', err);
      setError('Failed to load transaction history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [tickerSymbol, accountId]);

  useEffect(() => {
    if (show && tickerSymbol) {
      fetchTransactionHistory();
    }
  }, [show, tickerSymbol, fetchTransactionHistory]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6
    }).format(value);
  };

  // formatDate function is now imported from dateUtils

  const getTransactionBadgeVariant = (type) => {
    return type === 'BUY' ? 'success' : 'danger';
  };

  const calculateTotals = () => {
    if (!transactionData || !transactionData.transactions) return null;

    const totals = transactionData.transactions.reduce((acc, transaction) => {
      if (transaction.transaction_type === 'BUY') {
        acc.totalBuyShares += transaction.shares;
        acc.totalBuyAmount += transaction.total_amount;
        acc.buyCount++;
      } else if (transaction.transaction_type === 'SELL') {
        acc.totalSellShares += transaction.shares;
        acc.totalSellAmount += transaction.total_amount;
        acc.sellCount++;
      }
      return acc;
    }, {
      totalBuyShares: 0,
      totalBuyAmount: 0,
      totalSellShares: 0,
      totalSellAmount: 0,
      buyCount: 0,
      sellCount: 0
    });

    totals.netShares = totals.totalBuyShares - totals.totalSellShares;
    totals.netAmount = totals.totalSellAmount - totals.totalBuyAmount;

    return totals;
  };

  const totals = calculateTotals();

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Transaction History - {tickerSymbol}
          {transactionData && (
            <small className="text-muted d-block">
              {transactionData.ticker_name}
              {accountId ? ` (${accountName || 'Current Account Only'})` : ' (All Accounts)'}
            </small>
          )}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading transaction history...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">
            {error}
          </Alert>
        ) : !transactionData || transactionData.transactions.length === 0 ? (
          <Alert variant="info">
            No transaction history found for {tickerSymbol}.
          </Alert>
        ) : (
          <>
            {/* Summary Section */}
            {totals && (
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="mb-3">Summary</h6>
                <div className="row">
                  <div className="col-md-4">
                    <strong>Total Transactions:</strong> {transactionData.total_transactions}
                  </div>
                  <div className="col-md-4">
                    <strong>Buy Transactions:</strong> {totals.buyCount}
                  </div>
                  <div className="col-md-4">
                    <strong>Sell Transactions:</strong> {totals.sellCount}
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-4">
                    <strong>Net Shares:</strong> {formatNumber(totals.netShares)}
                  </div>
                  <div className="col-md-4">
                    <strong>Total Buy Amount:</strong> {formatCurrency(totals.totalBuyAmount)}
                  </div>
                  <div className="col-md-4">
                    <strong>Total Sell Amount:</strong> {formatCurrency(totals.totalSellAmount)}
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-12">
                    <strong>Net Amount:</strong> 
                    <span className={`ms-2 ${totals.netAmount >= 0 ? 'text-success' : 'text-danger'}`}>
                      {totals.netAmount >= 0 ? '+' : ''}{formatCurrency(totals.netAmount)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Transactions Table */}
            <div className="table-responsive">
              <Table hover size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Account</th>
                    <th>Portfolio</th>
                    <th className="text-end">Shares</th>
                    <th className="text-end">Price</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {transactionData.transactions.map((transaction) => (
                    <tr key={transaction.transaction_id}>
                      <td>{formatDate(transaction.transaction_date)}</td>
                      <td>
                        <Badge bg={getTransactionBadgeVariant(transaction.transaction_type)}>
                          {transaction.transaction_type}
                        </Badge>
                      </td>
                      <td>
                        <Link 
                          to={`/account/${transaction.account_id}`}
                          className="text-decoration-none"
                          onClick={onHide}
                        >
                          {transaction.account_name}
                        </Link>
                      </td>
                      <td>
                        <Link 
                          to={`/portfolio/${transaction.portfolio_id}`}
                          className="text-decoration-none text-muted"
                          onClick={onHide}
                        >
                          {transaction.portfolio_name}
                        </Link>
                      </td>
                      <td className="text-end">{formatNumber(transaction.shares)}</td>
                      <td className="text-end">{formatCurrency(transaction.price_per_share)}</td>
                      <td className="text-end">
                        <strong className={transaction.transaction_type === 'BUY' ? 'text-success' : 'text-danger'}>
                          {formatCurrency(transaction.total_amount)}
                        </strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default TickerTransactionHistoryModal; 